import { ManualVoucherData, VoucherSearchFilters, VoucherSummary, CancelVoucherRequest, CancelVoucherResponse } from '../types';
import { getVouchersByFilters } from '../mocks/vouchers.mock';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

// Mock mode flag - set to true to use mock data
const USE_MOCK_DATA = false;

class VouchersService {
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Error de conexión' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async emitManualVoucher(data: ManualVoucherData): Promise<{ success: boolean; voucherId: string; message: string }> {
    // Transform frontend data to backend format
    const backendData = {
      items: data.items.map(item => ({
        productId: item.id,
        nombre: item.description,
        cantidad: item.quantity,
        precioUnitario: item.unitPrice,
        descuento: item.discount || 0
      })),
      metodoPago: 'efectivo', // Default payment method for manual vouchers
      comprobante: {
        tipo: data.voucherType,
        serie: data.series || undefined
      },
      cliente: {
        tipo: data.customer.documentType === 'ruc' ? 'EMPRESA' : 'PERSONA',
        nombre: data.customer.name,
        documento: data.customer.documentNumber,
        email: data.customer.email || undefined,
        direccion: data.customer.address || undefined
      },
      observaciones: data.observations || undefined
    };

    return this.request('/vouchers', {
      method: 'POST',
      body: JSON.stringify(backendData),
    });
  }

  async searchVouchers(filters: VoucherSearchFilters): Promise<{
    data: VoucherSummary[];
    total: number;
    page: number;
    limit: number;
  }> {
    // Use mock data for development
    if (USE_MOCK_DATA) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return getVouchersByFilters(filters);
    }

    const params = new URLSearchParams();
    
    if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.append('dateTo', filters.dateTo);
    if (filters.voucherType) params.append('voucherType', filters.voucherType);
    if (filters.status) params.append('status', filters.status);
    if (filters.locationId) params.append('locationId', filters.locationId);
    if (filters.series) params.append('series', filters.series);
    if (filters.number) params.append('number', filters.number);

    // Use the correct endpoint: GET /vouchers instead of /vouchers/search
    const response = await this.request(`/vouchers?${params.toString()}`);
    
    // Transform backend Sale data to frontend VoucherSummary format
    const transformedVouchers = (response.data?.vouchers || []).map((sale: any) => ({
      id: sale.id,
      series: sale.comprobanteNumero ? sale.comprobanteNumero.split('-')[0] : 'B001',
      number: sale.comprobanteNumero ? sale.comprobanteNumero.split('-')[1] : sale.numero,
      voucherType: sale.comprobanteTipo?.toLowerCase() === 'factura' ? 'factura' : 'boleta' as 'boleta' | 'factura',
      issueDate: sale.fecha,
      customerName: sale.clienteNombre || 'Cliente no especificado',
      total: sale.total,
      status: sale.comprobanteEstado?.toLowerCase() === 'anulado' ? 'anulado' : 'emitido' as 'emitido' | 'anulado',
      locationId: sale.locationId,
      locationName: sale.locationName || 'Local no especificado',
      canCancel: sale.comprobanteEstado !== 'ANULADO' && sale.estado !== 'ANULADA',
      daysRemaining: 7 // Default 7 days for cancellation
    }));
    
    // Transform backend response to match frontend expected structure
    return {
      data: transformedVouchers,
      total: response.data?.pagination?.total || 0,
      page: response.data?.pagination?.page || 1,
      limit: response.data?.pagination?.limit || 10
    };
  }

  async cancelVoucher(request: CancelVoucherRequest): Promise<CancelVoucherResponse> {
    return this.request(`/vouchers/${request.voucherId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason: request.reason }),
    });
  }

  async validateCancellation(voucherId: string): Promise<{
    canCancel: boolean;
    reason?: string;
    daysRemaining?: number;
  }> {
    return this.request(`/vouchers/${voucherId}/validate-cancellation`);
  }

  async getVoucherDetails(voucherId: string): Promise<{
    id: string;
    series: string;
    number: string;
    voucherType: 'boleta' | 'factura';
    issueDate: string;
    customer: any;
    items: any[];
    total: number;
    status: 'emitido' | 'anulado';
    nubefactData?: any;
  }> {
    return this.request(`/vouchers/${voucherId}`);
  }
}

export const vouchersService = new VouchersService();